import React, { useState, useEffect } from 'react';
import { Wifi, Lock, MapPin, Smartphone, ChevronRight, XCircle, CheckCircle2, AlertTriangle, ShieldCheck, Instagram } from 'lucide-react';

export default function App() {
  // Estados da simulação: 'connecting', 'authenticating', 'failed'
  const [status, setStatus] = useState('connecting');
  const [progress, setProgress] = useState(10);

  useEffect(() => {
    // Sequência de animação para simular conexão real
    // Cria suspense antes de mostrar a oferta
    const timer1 = setTimeout(() => { setProgress(40); setStatus('authenticating'); }, 1500);
    const timer2 = setTimeout(() => { setProgress(75); }, 2500);
    const timer3 = setTimeout(() => { setProgress(90); }, 3500);
    const timer4 = setTimeout(() => { setStatus('failed'); }, 4200); // Momento da virada (4.2s)

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, []);

  const handleWhatsAppClick = () => {
    const phone = "5598985992136";
    const text = encodeURIComponent("Peguei seu wifi e quero me proteger");
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
  };

  const handleInstagramClick = () => {
    window.open('https://instagram.com/rastremix', '_blank');
  };

  // --- TELA 1: SIMULAÇÃO DE CONEXÃO (O GANCHO) ---
  // Essa tela imita a interface do sistema operacional (Android/iOS)
  if (status !== 'failed') {
    return (
      <div className="bg-gray-100 flex justify-center min-h-screen font-sans">
        <div className="max-w-md w-full bg-white min-h-screen flex flex-col items-center justify-center px-6 select-none overflow-hidden relative">
          <div className="w-full text-center relative z-10">
          
          {/* Animação do Radar Wi-Fi */}
          <div className="relative inline-flex items-center justify-center mb-10">
            <div className="absolute inset-0 bg-red-500 rounded-full opacity-20 animate-ping duration-[2000ms]"></div>
            <div className="absolute inset-0 bg-red-500 rounded-full opacity-10 animate-ping delay-75 duration-[2000ms]"></div>
            <div className="relative bg-white p-8 rounded-full shadow-2xl border-4 border-gray-50 z-10">
              <Wifi size={64} className="text-red-600 animate-pulse" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-800 mb-2 tracking-tight">
            {status === 'connecting' ? 'Buscando Rede...' : 'Verificando Segurança...'}
          </h2>
          
          <p className="text-sm text-gray-500 font-medium h-6 mb-8 transition-all">
            {status === 'connecting' && "Obtendo endereço IP..."}
            {status === 'authenticating' && "Autenticando credenciais..."}
          </p>

          {/* Barra de Progresso Estilo Sistema */}
          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner border border-gray-100">
            <div 
              className="h-full bg-gradient-to-r from-red-500 to-red-600 transition-all duration-700 ease-out rounded-full shadow-[0_0_10px_rgba(255,0,0,0.5)]"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <p className="mt-8 text-[10px] text-gray-400 uppercase tracking-[0.2em] animate-pulse">
            Não feche esta tela
          </p>
        </div>
        
        {/* Fundo decorativo sutil */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
             <div className="absolute -top-[20%] -right-[20%] w-[50%] h-[50%] bg-red-50 rounded-full blur-3xl opacity-50"></div>
             <div className="absolute top-[40%] -left-[20%] w-[40%] h-[40%] bg-gray-100 rounded-full blur-3xl opacity-50"></div>
        </div>
        </div>
      </div>
    );
  }

  // --- TELA 2: A MENSAGEM (O PITCH DE VENDAS) ---
  // O cliente cai aqui após a "falha" na conexão
  return (
    <div className="bg-gray-100 flex justify-center min-h-screen font-sans">
      <div className="max-w-md w-full bg-white min-h-screen flex flex-col relative animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      {/* HEADER: Identidade Dark Mode (Premium) */}
      <div className="bg-[#0a0a0a] pt-12 pb-20 px-6 rounded-b-[3rem] shadow-2xl relative overflow-hidden w-full">
        {/* Efeitos de Luz de Fundo */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-80 h-80 bg-red-600 blur-[120px] opacity-20 rounded-full pointer-events-none"></div>
        
        <div className="flex flex-col items-center text-center relative z-10">
          
          {/* Alerta Visual */}
          <div className="bg-red-500/10 p-4 rounded-full mb-6 ring-1 ring-red-500/30 backdrop-blur-sm animate-[bounce_2s_infinite]">
            <XCircle size={56} className="text-red-500" />
          </div>
          
          <h1 className="text-4xl font-black text-white mb-3 tracking-tight leading-none">
            Conexão <br/><span className="text-red-600">Interrompida</span>
          </h1>
          <p className="text-gray-400 text-sm mt-2 max-w-[280px] font-medium leading-relaxed">
            Esta rede pública não oferece criptografia segura para seus dados pessoais.
          </p>
        </div>
      </div>

      {/* CARD PRINCIPAL: A Revelação */}
      <div className="px-6 -mt-12 relative z-20 w-full">
        <div className="bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] p-8 border border-gray-100">
          
          {/* LOGO RASTREMIX */}
          <div className="flex justify-center mb-8 border-b border-gray-50 pb-6">
            <img src="/logo-rastremix.png" alt="RASTREM Logo" className="h-16 object-contain" />
          </div>

          <div className="space-y-6 text-center">
            <h3 className="text-xl font-bold text-gray-900 leading-tight">
              Calma, foi apenas uma simulação.<br/>
              <span className="text-gray-400 font-normal text-sm block mt-1">Mas o perigo real não avisa.</span>
            </h3>
            
            <p className="text-sm text-gray-600 leading-relaxed">
              Você procurou Wi-Fi grátis e encontrou um alerta. Na rua, criminosos não dão segunda chance.
            </p>

            {/* A Frase Chave */}
            <div className="bg-red-50 border-l-4 border-red-600 p-5 rounded-r-xl text-left shadow-sm relative overflow-hidden group">
               <div className="absolute right-0 top-0 opacity-5 -mr-4 -mt-4 transform rotate-12 group-hover:scale-110 transition-transform duration-500">
                 <ShieldCheck size={80} className="text-red-600" />
               </div>
              <p className="text-sm text-gray-800 relative z-10">
                <span className="block text-red-600 font-black text-[10px] uppercase mb-2 tracking-wider flex items-center">
                  <AlertTriangle size={12} className="mr-1"/> Mensagem da Rastremix
                </span>
                "Você acabou de encontrar algo mais valioso que a internet: <strong className="text-gray-900">Proteção para sua família e seu patrimônio.</strong>"
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BENEFÍCIOS (Ícones Tech) */}
      <div className="px-6 mt-10 mb-8 space-y-4 w-full">
        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest text-center mb-6">
          Tecnologia de Proteção Ativada
        </h4>
        
        <Feature 
          icon={<MapPin size={20} className="text-white" />}
          color="bg-neutral-900"
          title="Rastreamento em Tempo Real"
          text="Saiba a localização exata a cada segundo."
        />
        <Feature 
          icon={<Lock size={20} className="text-white" />}
          color="bg-red-600"
          title="Bloqueio Anti-Roubo"
          text="Corte a ignição pelo celular instantaneamente."
        />
        <Feature 
          icon={<Smartphone size={20} className="text-white" />}
          color="bg-gray-400"
          title="App de Controle Total"
          text="Histórico de rotas, velocidade e cercas virtuais."
        />
      </div>

      {/* FOOTER FIXO */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-[0_-10px_40px_rgba(0,0,0,0.1)] z-50 w-full">
        
        {/* ÁREA DO INSTAGRAM - NOVO */}
        <div className="bg-gray-50 border-b border-gray-100 py-3 px-4 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors" onClick={handleInstagramClick}>
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 p-[2px] rounded-full">
              <div className="bg-white p-1 rounded-full">
                <Instagram size={16} className="text-black" />
              </div>
            </div>
            <div className="text-left">
              <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wide">Achou criativo?</p>
              <p className="text-xs font-semibold text-gray-800">Poste e marque <span className="text-red-600">@rastremix</span></p>
            </div>
          </div>
          <ChevronRight size={16} className="text-gray-400" />
        </div>

        {/* BOTÃO WHATSAPP */}
        <div className="p-4 max-w-md mx-auto">
          <div className="flex justify-center mb-3">
             <span className="bg-green-100 text-green-800 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide flex items-center shadow-sm animate-pulse">
               <CheckCircle2 size={12} className="mr-1" />
               Lead Verificado via Adesivo
             </span>
          </div>
          
          <button 
            onClick={handleWhatsAppClick}
            className="w-full group relative overflow-hidden bg-[#25D366] hover:bg-[#128C7E] active:scale-[0.98] transition-all rounded-2xl p-4 shadow-xl shadow-green-500/20 flex items-center justify-between"
          >
            {/* Efeito Brilho */}
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-in-out"></div>
            
            <div className="flex items-center z-10">
               <div className="bg-black/10 p-2 rounded-xl mr-3 group-hover:bg-black/20 transition-colors">
                 <Smartphone className="text-white" size={24} />
               </div>
               <div className="text-left">
                 <span className="block text-[10px] font-bold text-green-50 uppercase opacity-90 tracking-wider">Falar com Especialista</span>
                 <span className="block text-xl font-black text-white leading-none tracking-tight mt-0.5">QUERO ME PROTEGER</span>
               </div>
            </div>
            
            <ChevronRight className="text-white/80 z-10 group-hover:translate-x-1 transition-transform" size={24} />
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

// Componente de Item de Benefício
function Feature({ icon, color, title, text }) {
  return (
    <div className="flex items-start bg-white p-3 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className={`${color} p-2.5 rounded-xl shadow-md mr-4 shrink-0 mt-0.5`}>
        {icon}
      </div>
      <div>
        <h4 className="font-bold text-gray-900 text-sm">{title}</h4>
        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{text}</p>
      </div>
    </div>
  );
}